import { useSession } from 'next-auth/react';
import { useEffect } from 'react';
import {
  trackMetricsEvent,
  trackPageView,
  trackButtonClick,
  trackFeatureUsed,
  trackQuestion,
  trackFeedback,
  trackError,
  MetricsCategory,
  MetricsAction,
} from '../utils/metrics.utils';
import { usePathname, useSearchParams } from 'next/navigation';

/**
 * Custom hook for tracking metrics
 * Provides methods for tracking various types of events
 */
export function useMetrics() {
  const { data: session } = useSession();

  /**
   * Track a generic event
   */
  const trackEvent = (eventData: {
    category: MetricsCategory | string;
    action: MetricsAction | string;
    label?: string;
    value?: number;
    dimensions?: Record<string, any>;
  }) => {
    trackMetricsEvent({
      ...eventData,
      userId: session?.user?.id,
    });
  };

  /**
   * Track a page view
   */
  const trackPage = (pageName: string, userId: string, groupName: string) => {
    trackPageView(pageName, userId, groupName);
  };

  /**
   * Track a click event
   */
  const trackClick = (buttonName: string) => {
    trackButtonClick(buttonName);
  };

  /**
   * Track a button click
   */
  const trackButton = (buttonName: string) => {
    trackButtonClick(buttonName);
  };

  /**
   * Track feature usage
   */
  const trackFeature = (featureName: string) => {
    trackFeatureUsed(featureName);
  };

  /**
   * Track a user question
   */
  const trackUserQuestion = (questionText: string, topic: string) => {
    trackQuestion(questionText, topic);
  };

  /**
   * Track user feedback
   */
  const trackUserFeedback = (rating: number, comment: string = '') => {
    trackFeedback(rating, comment);
  };

  /**
   * Track an error
   */
  const trackClientError = (errorMessage: string) => {
    trackError('client', errorMessage);
  };

  /**
   * Track a server error
   */
  const trackServerError = (errorMessage: string) => {
    trackError('server', errorMessage);
  };

  return {
    trackEvent,
    trackClick,
    trackPage,
    trackButton,
    trackFeature,
    trackUserQuestion,
    trackUserFeedback,
    trackClientError,
    trackServerError,
  };
}

/**
 * Hook to automatically track page views
 */
export function usePageViewTracking(
  pageName?: string,
  userId?: string,
  groupName?: string
) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const url = pathname + (searchParams.toString() ? `?${searchParams}` : '');

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: url,
        page_location: window.location.href,
        page_title: pageName || document.title,
        app_section: 'app', // Custom dimension
      });
    }
    const verifiefUserId = userId || '';
    const finalPageName = pageName || document.title || pathname;
    trackPageView(finalPageName, verifiefUserId, groupName || 'NA');
  }, [pathname, searchParams, pageName, groupName, userId]);
}

export default useMetrics;
